import { isPresent, isWebLink, unique } from "@/lib/utils"
import vuexStore from "@/store"
import router from "@/router"
import i18n from "@/store/i18n"
import ApiClient from "@/lib/api-client"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { formatAmount } from "@/lib/checkout-utils"
import { pageDataToProps } from "@shared/page-data"
import { occurrenceRef, pageRef } from "@/store/pages"
import { Product } from "@shared/types"
import { toDate } from "@/lib/date-utils"
import { MANAGED_PAGE_PREFIX } from "@/lib/constants"
import { getCachedPageByPath } from "@/lib/page-cache"
import isBuilderContext from "@/lib/is-platform/is-builder-context"
import {
  getPageAccess,
  loadPageAccessTree,
  skipsAccessChecks,
  toAccessState
} from "@/lib/access/utils"
import { PageId } from "@/types"
import {
  Row,
  WrittenRow,
  QueryOptions,
  DeleteQuery,
  TrackEventPayload,
  MvtRoute,
  NavigateOptions,
  Occurrence,
  ScheduleQueryOptions,
  ScheduleAddOptions,
  ScheduleRowRef,
  ScheduleBulkAddItem,
  ScheduleBulkAddError,
  ScheduleBulkAddFailure,
  ProductDetails,
  PurchaseResult,
  Page,
  PagesQueryOptions,
  PagesQueryResult,
  SavedListOptions,
  ProgressStatus,
  ProgressSubject,
  StartOptions,
  CompleteOptions,
  StreakOptions,
  Streak,
  DateInput,
  MvtUser,
  OnUpdate,
  TableQueryResult,
  AccessState,
  MvtQueryRow
} from "@/sdk/types"
import { EventManager, MVT_EVENTS } from "@/sdk/event-manager"
import { HydrationGuard, ANNOUNCE_DELAY_MS } from "@/sdk/hydration-guard"
import { NativeBridge } from "@/sdk/native-bridge"
import { Cast } from "@/sdk/cast"
import { ReadCache, stableStringify } from "@/sdk/read-cache"
import { ProgressBatch, ProgressRow } from "@/sdk/progress-batch"
import { Batcher, FlushMeta } from "@/sdk/transport/batcher"
import { fanOut } from "@/sdk/transport/fan-out"
import { sessionIdentity } from "@/sdk/transport/identity"
import {
  CodedError,
  codedError,
  notAuthenticated
} from "@/sdk/transport/errors"

export { MVT_EVENTS } from "@/sdk/event-manager"

// On 422 validation failure, caught errors are augmented with `fieldErrors`:
//
//   try { await mvt.tables.meal_log.create(value) }
//   catch (err) {
//     if (err.fieldErrors) {
//       // { calories: ["must be a number"], name: ["is required"] }
//     } else throw err
//   }
//
// Phrasing is sourced from `lib.validation.*` in the locale files.

type ValidationCode = { code: string; expected?: any }

type ApiError = {
  response?: { status?: number; data?: Record<string, unknown> }
  fieldErrors?: Record<string, string[]>
}

function renderCode({ code, expected }: ValidationCode): string {
  const value = Array.isArray(expected)
    ? expected.join(", ")
    : String(expected ?? "")

  const key = `lib.validation.${code}`
  const finalKey = i18n.te(key) ? key : "lib.validation.unknown"

  return i18n.t(finalKey, { expected: value }) as string
}

function renderFieldErrors(
  fields: Record<string, ValidationCode[]>
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(fields).map(([field, list]) => [field, list.map(renderCode)])
  )
}

function attachFieldErrors(err: any): never {
  const data = err?.response?.data

  if (data?.reason === "validation_error" && data.errors) {
    err.fieldErrors = renderFieldErrors(
      data.errors as Record<string, ValidationCode[]>
    )
  }

  throw err
}

// The refusal alone leaves an author guessing where the rows went.
function contentTableError(slug: string): CodedError {
  return codedError(
    "content_table",
    `$mvt.tables.${slug} is a content type — read it with $mvt.pages.query({ type: "${slug}" })`
  )
}

const readCache = new ReadCache()

const ROUTES = {
  pages: () => "/v2/pages",
  pagesResolve: () => "/v2/pages/resolve",
  progress: () => "/v2/progress",
  tableRows: (slug: string) => `/v2/tables/${slug}/rows`,
  tableRow: (slug: string, id: string) =>
    `${ROUTES.tableRows(slug)}/${encodeURIComponent(id)}`,
  schedule: () => "/v2/schedule",
  scheduleItems: () => "/v2/schedule/items",
  scheduleItem: (id: string) =>
    `${ROUTES.scheduleItems()}/${encodeURIComponent(id)}`,
  scheduleItemsBulk: () => `${ROUTES.scheduleItems()}/bulk`,
  scheduleItemsBulkDelete: () => `${ROUTES.scheduleItems()}/bulk_delete`
} as const

// A batchable write: `result` maps each success slot onto the method's contract.
type WriteOp<T> = {
  name: string
  endpoint: string
  cap: number
  emits?: string
  result: (item: unknown) => T
}

const WRITE_OPS = {
  // The wire occurrence; the surface maps it into the SDK shape per instance.
  scheduleAdd: {
    name: "scheduleAdd",
    endpoint: ROUTES.scheduleItemsBulk(),
    cap: 100,
    emits: MVT_EVENTS.SCHEDULE_CHANGED,
    result: item => item as Occurrence
  } as WriteOp<Occurrence>,
  // remove() resolves with nothing; the mapper is the contract.
  scheduleRemove: {
    name: "scheduleRemove",
    endpoint: ROUTES.scheduleItemsBulkDelete(),
    cap: 100,
    emits: MVT_EVENTS.SCHEDULE_CHANGED,
    result: () => undefined
  } as WriteOp<void>
}

const retryAfterMs = (err: ApiError): number | null => {
  const seconds = Number(
    (err.response as { headers?: Record<string, string> })?.headers?.[
      "retry-after"
    ]
  )

  return seconds > 0 ? seconds * 1000 : null
}

// /v2 answers one error envelope, so no surface has to read an error body.
function v2Error(err: unknown): unknown {
  const envelope = (err as ApiError).response?.data as
    | {
        error?: {
          code?: string
          message?: string
          fields?: Record<string, ValidationCode[]>
        }
      }
    | undefined

  const error = envelope?.error

  if (!error?.code) return err

  const coded = codedError(error.code, error.message ?? "Request failed")

  if (error.fields) coded.fieldErrors = renderFieldErrors(error.fields)

  const wait = retryAfterMs(err as ApiError)

  if (wait !== null) coded.retryAfterMs = wait

  return coded
}

// One lazy batcher per descriptor, shared by every Mvt instance like readCache.
const batchers = new Map<WriteOp<any>, Batcher<object, unknown>>()

const RATE_LIMIT_RETRIES = 1
// Longer than this and the caller decides, not the queue: every later flush
// of the descriptor waits behind a held one.
const RATE_LIMIT_MAX_WAIT_MS = 10000

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

class ApiService {
  static op<T>(descriptor: WriteOp<T>, item: object): Promise<T> {
    let batcher = batchers.get(descriptor)

    if (!batcher) {
      batcher = new Batcher<object, unknown>({
        cap: descriptor.cap,
        identity: sessionIdentity,
        flush: (items, meta) =>
          ApiService.flushEnvelope(descriptor, items, meta)
      })
      batchers.set(descriptor, batcher)
    }

    return batcher.push(item) as Promise<T>
  }

  private static async flushEnvelope<T>(
    descriptor: WriteOp<T>,
    items: object[],
    meta: FlushMeta
  ) {
    const identity = sessionIdentity()
    const startedAt = Date.now()
    let retries = 0
    let status: "ok" | "failed" = "failed"

    try {
      let data

      for (;;) {
        // ApiClient reads credentials at send time; a held retry is a second send.
        if (sessionIdentity() !== identity) throw notAuthenticated()

        try {
          data = await readCache.write(() =>
            ApiClient.post(descriptor.endpoint, { items })
          )
          break
        } catch (err) {
          // Refuse-whole makes a 429 safe to retry: nothing landed.
          const throttled = (err as ApiError).response?.status === 429
          const wait = throttled ? retryAfterMs(err as ApiError) : null

          if (
            wait === null ||
            wait > RATE_LIMIT_MAX_WAIT_MS ||
            retries >= RATE_LIMIT_RETRIES
          ) {
            throw v2Error(err)
          }

          retries++
          await sleep(wait)
        }
      }

      // A response landing after a member switch is nobody's to act on.
      if (sessionIdentity() !== identity) throw notAuthenticated()

      const settled = fanOut(items.length, data?.data, descriptor.result)

      if (descriptor.emits && settled.some(outcome => outcome.ok)) {
        eventManager.emit(descriptor.emits)
      }

      status = "ok"

      return settled
    } finally {
      console.debug("[mvt:transport]", {
        descriptor: descriptor.name,
        batchSize: items.length,
        waitedMs: meta.waitedMs,
        staleRejected: meta.staleRejected,
        retries,
        status,
        durationMs: Date.now() - startedAt
      })
    }
  }

  static async post(endpoint: string, body: Record<string, any>) {
    try {
      const response = await readCache.write(() =>
        ApiClient.post(endpoint, body)
      )

      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }

  static async get(
    endpoint: string,
    params: Record<string, any>,
    stale = false
  ) {
    return await readCache.get(endpoint, params, stale)
  }

  static async getV2(
    endpoint: string,
    params: Record<string, any>,
    stale = false
  ) {
    return await ApiService.readV2(() => readCache.get(endpoint, params, stale))
  }

  static async walkV2(
    endpoint: string,
    params: Record<string, any>,
    stale = false
  ) {
    return await ApiService.readV2(() =>
      readCache.walk(endpoint, params, stale)
    )
  }

  static async postV2(endpoint: string, body: Record<string, any>) {
    return await ApiService.writeV2(() => ApiClient.post(endpoint, body))
  }

  static async putV2(endpoint: string, body: Record<string, any>) {
    return await ApiService.writeV2(() => ApiClient.put(endpoint, body))
  }

  static async deleteV2(endpoint: string, params?: Record<string, any>) {
    return await ApiService.writeV2(() =>
      params
        ? ApiClient.delete(endpoint, { params })
        : ApiClient.delete(endpoint)
    )
  }

  private static async readV2(run: () => Promise<any>) {
    try {
      return await run()
    } catch (err) {
      throw v2Error(err)
    }
  }

  private static async writeV2(run: () => Promise<any>) {
    try {
      const response = await readCache.write(run)

      return response.data
    } catch (err) {
      throw v2Error(err)
    }
  }

  static async put(endpoint: string, body: Record<string, any>) {
    try {
      const response = await readCache.write(() =>
        ApiClient.put(endpoint, body)
      )

      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }

  static async delete(endpoint: string, params: Record<string, any>) {
    try {
      const response = await readCache.write(() =>
        ApiClient.delete(endpoint, { params })
      )

      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }
}

class Table {
  constructor(private readonly slug: string) {}

  private get endpoint() {
    return ROUTES.tableRows(this.slug)
  }

  private rowPath(id: string) {
    return ROUTES.tableRow(this.slug, id)
  }

  async create(input: Row[]): Promise<WrittenRow[]>
  async create(input: any, created_at?: Date): Promise<WrittenRow>
  async create(
    input: any | Row[],
    created_at?: Date
  ): Promise<WrittenRow | WrittenRow[]> {
    const batch = Array.isArray(input)
    const data = batch ? input : [{ value: input, created_at }]
    const written = await ApiService.postV2(this.endpoint, { data })
    const results = written?.results ?? []

    return batch ? results : results[0]
  }

  // A row's value is replaced whole rather than merged, so the verb is PUT.
  async update(id: string, value: any): Promise<WrittenRow> {
    return await ApiService.putV2(this.rowPath(id), { value })
  }

  async delete(query?: DeleteQuery) {
    const id = query?.id

    if (id) return await ApiService.deleteV2(this.rowPath(id))

    return await ApiService.deleteV2(
      this.endpoint,
      query?.where ? { where: query.where } : {}
    )
  }

  async query(
    options: QueryOptions = {},
    onUpdate?: OnUpdate<TableQueryResult>
  ): Promise<TableQueryResult> {
    return await readCache.revalidating(
      stale => this.read(options, stale),
      onUpdate
    )
  }

  async first(
    options: QueryOptions = {},
    onUpdate?: OnUpdate<MvtQueryRow | null>
  ): Promise<MvtQueryRow | null> {
    return await readCache.revalidating(
      async stale =>
        (await this.read({ ...options, limit: 1 }, stale)).results[0] ?? null,
      onUpdate
    )
  }

  private async read(
    options: QueryOptions,
    stale: boolean
  ): Promise<TableQueryResult> {
    const { all_members, ...query } = options

    try {
      const data = await ApiService.getV2(
        this.endpoint,
        {
          ...query,
          ...(all_members ? { all_members: true } : {})
        },
        stale
      )

      return {
        results: data?.results ?? [],
        ...(isPresent(data?.next_cursor)
          ? { nextCursor: data.next_cursor }
          : {})
      }
    } catch (err) {
      const code = (err as CodedError)?.code

      if (code === "content_table") throw contentTableError(this.slug)

      // A slug an author has yet to create reads as an empty table, not a throw.
      if (code === "not_found") return { results: [] }

      throw err
    }
  }
}

const eventManager = new EventManager()

let purchaseSeq = 0

class ProductsApi {
  // Member-scoped details (price/availability can vary per member), so the
  // cache is dropped on logout and refetched lazily per id on first read.
  private readonly cache = new Map<number, ProductDetails>()

  constructor() {
    eventManager.on(MVT_EVENTS.LOGGED_OUT, () => this.cache.clear())
  }

  hasPurchased(id: number): boolean {
    return vuexStore.getters["auth/hasPurchasedProduct"](id)
  }

  async getDetails(id: number): Promise<ProductDetails> {
    const cached = this.cache.get(id)

    if (cached) return cached

    const product: Product = await vuexStore.dispatch(
      "products/fetchProduct",
      id
    )

    const details = this.toDetails(product)

    this.cache.set(id, details)

    return details
  }

  async purchase(idOrIds: number | number[]): Promise<PurchaseResult> {
    const ids = unique(Array.isArray(idOrIds) ? idOrIds : [idOrIds])

    if (!ids.length) return { status: "cancelled" }

    const owned = this.firstPurchased(ids)

    if (isPresent(owned)) return { status: "paid", productId: owned }

    const token = `mvt-purchase-${++purchaseSeq}`

    return new Promise<PurchaseResult>(resolve => {
      let settled = false

      const settle = (result: PurchaseResult) => {
        if (settled) return
        settled = true
        EventBus.$off(EVENTS.CHECKOUT_COMPLETED, onCompleted)
        EventBus.$off(EVENTS.CHECKOUT_CLOSED, onClosed)
        resolve(result)
      }

      // The token identifies this invocation so a stale close from a prior
      // checkout of the same product (the kept-alive Checkout deactivating as
      // the next one opens) cannot settle it. On close, ownership decides the
      // outcome: some checkout exits grant ownership without a `completed`
      // event (e.g. an already-active purchase confirmed server-side), so
      // ownership-now-true on close is a success.
      const onCompleted = (id: number, eventToken?: string) => {
        if (eventToken !== token) return
        settle({ status: "paid", productId: id })
      }

      const onClosed = (eventToken?: string) => {
        if (eventToken !== token) return
        const bought = this.firstPurchased(ids)

        settle(
          isPresent(bought)
            ? { status: "paid", productId: bought }
            : { status: "cancelled" }
        )
      }

      EventBus.$on(EVENTS.CHECKOUT_COMPLETED, onCompleted)
      EventBus.$on(EVENTS.CHECKOUT_CLOSED, onClosed)

      EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
        productIds: ids,
        token,
        // Multiple ids means the caller is letting the member choose between
        // options, so a hidden product shouldn't surface as one of them.
        includeHidden: ids.length === 1
      })
    })
  }

  private firstPurchased(ids: number[]): number | undefined {
    return ids.find(id => this.hasPurchased(id))
  }

  private toDetails(product: Product): ProductDetails {
    return {
      id: product.id,
      name: product.title,
      priceLabel: this.priceLabel(product)
    }
  }

  private priceLabel(product: Product): string {
    if (!isPresent(product.price)) return ""

    const symbol = product.symbol || "$"

    const currency = product.currency
      ? ` ${product.currency.toUpperCase()}`
      : ""

    return `${symbol}${formatAmount(product.price)}${currency}`
  }
}

const loggedIn = (): boolean => isPresent(vuexStore.getters["auth/currentUser"])

// The URL is how a day arrives, but a modal is a hash change that drops the
// query, so the store holds it for as long as the member is on the page.
const ambientOccurrenceId = (): string | null => {
  const id = router.currentRoute?.query?.occurrence
  const value = Array.isArray(id) ? id[0] : id

  if (isPresent(value)) return String(value)

  return vuexStore.getters["pages/ambientOccurrenceId"](
    router.currentRoute?.path
  )
}

const currentPagePath = (): string | null =>
  (vuexStore as any).state?.pages?.currentPage?.path ?? null

// A path may arrive addressed at an occurrence, but the page it names is the
// same page, so identity is always the bare path.
const barePath = (path: string | null | undefined): string | null =>
  isPresent(path) ? path.split("?")[0] : null

const occurrenceFromPath = (path: string | null | undefined): string | null => {
  const query = isPresent(path) ? path.split("?")[1] : null
  const id = new URLSearchParams(query ?? "").get("occurrence")

  return isPresent(id) ? id : null
}

// The day a path is asking about: named on the path, or — because the page
// being viewed was opened for one — the ambient occurrence. Reads and writes
// share this so `status` and `complete` can never answer for different days.
const occurrenceFor = (path: string | null | undefined): string | null =>
  occurrenceFromPath(path) ??
  ((barePath(path) ?? path) === currentPagePath()
    ? ambientOccurrenceId()
    : null)

const occurrenceId = (
  occurrence: Pick<Occurrence, "id"> | string | number
): string => {
  const id = typeof occurrence === "object" ? occurrence?.id : occurrence

  if (!isPresent(id)) throw codedError("not_found", "Unknown occurrence")

  return String(id)
}

const resolveProgressSubject = (
  subject: ProgressSubject
): { path: string } | { occurrenceId: string } => {
  if (typeof subject === "string") return { path: subject }

  const path = subject.page?.path

  return isPresent(path) ? { path } : { occurrenceId: occurrenceId(subject) }
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

// Mirrors ScheduledItem::BulkAdd::MAX_ITEMS.
const MAX_BULK_SCHEDULE_ITEMS = 100

function parseDate(value: DateInput, label: string): Date {
  const date = new Date(value)

  if (isNaN(date.getTime())) {
    throw codedError("invalid_date", `Invalid ${label}: ${String(value)}`)
  }

  return date
}

function toIsoDate(value: DateInput, label: string): string {
  return parseDate(value, label).toISOString()
}

// Formats the original value: dayjs reads a bare date as local, `new Date` as UTC.
function toCalendarDay(value: DateInput, label: string): string {
  parseDate(value, label)

  return toDate(value, "YYYY-MM-DD")
}

// The host already caches every visited page by path (IndexedDB + the inlined
// FIT_initialState), so a sync lookup usually avoids a round-trip.
class PagePaths {
  private readonly uuidByPath: Record<string, string> = {}
  private readonly rootByUuid: Record<string, string> = {}
  private readonly unresolvable = new Set<string>()
  private readonly inFlight = new Map<string, Promise<string | null>>()
  private session = 0

  constructor() {
    const forget = () => {
      this.unresolvable.clear()
      this.session++
    }

    eventManager.on(MVT_EVENTS.LOGGED_IN, forget)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, forget)
  }

  known(pathOrAddress: string): string | null {
    const path = barePath(pathOrAddress) ?? pathOrAddress

    if (this.uuidByPath[path]) return this.uuidByPath[path]

    const inlined = window.FIT_initialState?.[path]?.props

    if (!inlined?.page_id) return null

    this.remember(path, inlined.page_id, inlined.root_id)

    return inlined.page_id
  }

  rootOf(uuid: string): string | null {
    return this.rootByUuid[uuid] ?? null
  }

  remember(
    pathOrAddress: string,
    uuid?: string | null,
    root?: string | null
  ): void {
    const path = barePath(pathOrAddress)

    if (!isPresent(path) || !isPresent(uuid)) return

    this.uuidByPath[path] = uuid
    this.unresolvable.delete(path)

    if (isPresent(root)) this.rootByUuid[uuid] = root
  }

  async resolve(pathOrAddress: string): Promise<string> {
    const path = barePath(pathOrAddress) ?? pathOrAddress
    const uuid = this.known(path) ?? (await this.lookup(path).catch(() => null))

    if (!uuid) throw codedError("not_found", `Unknown page: ${path}`)

    return uuid
  }

  // Reactive reads re-run this every render, so a path the server says has no
  // page is remembered. A lookup that threw is not — it can't be told apart
  // from a blip.
  lookup(pathOrAddress: string): Promise<string | null> {
    const path = barePath(pathOrAddress) ?? pathOrAddress

    if (this.unresolvable.has(path)) return Promise.resolve(null)

    const existing = this.inFlight.get(path)

    if (existing) return existing

    const session = this.session

    const lookup = this.fetchUuid(path)
      .then(uuid => {
        // A lookup that started before a session change says nothing about this one.
        if (!uuid && session === this.session) this.unresolvable.add(path)

        return uuid
      })
      .finally(() => this.inFlight.delete(path))

    this.inFlight.set(path, lookup)

    return lookup
  }

  private async fetchUuid(path: string): Promise<string | null> {
    // The builder has no page space to resolve against, and /me/* are
    // client-side routes with no page behind them.
    if (isBuilderContext() || path.startsWith("/me/")) return null

    const cached = await getCachedPageByPath(path)

    if (cached?.props?.page_id) {
      this.remember(path, cached.props.page_id, cached.props.root_id)

      return cached.props.page_id
    }

    // Only `not_found` means "no page"; anything else stays a throw, which the
    // caller keeps distinct from an answer.
    const page = await ApiService.getV2(ROUTES.pagesResolve(), {
      path
    }).catch((err: { code?: string }) => {
      if (err?.code === "not_found") return null

      throw err
    })

    this.remember(path, page?.id, page?.access?.root)

    return page?.id ?? null
  }
}

type PagePayload = Page & { id?: string; progress?: ProgressStatus }

// The one translation of ProgressSerializer's status shape into the store, so
// every payload that carries progress seeds it the same way.
const progressRow = (status: ProgressStatus) => ({
  started_at: status.startedAt ?? null,
  completed_at: status.completedAt ?? null,
  percent_complete: status.percent ?? 0,
  is_completed: !!status.completed
})

const seedProgress = (
  entries: Array<{ ref: string; progress: object }>
): void => {
  if (entries.length) vuexStore.commit("pages/setProgresses", entries)
}

type OccurrencePayload = Occurrence & {
  page?: PagePayload
  progress?: ProgressStatus
}

const seedOccurrenceProgress = (
  occurrence: Pick<Occurrence, "id" | "startsAt">,
  progress?: ProgressStatus
): void => {
  if (!progress || !isPresent(occurrence.id)) return

  seedProgress([
    {
      ref: occurrenceRef(occurrence.id),
      progress: {
        ...progressRow(progress),
        starts_at: occurrence.startsAt ?? null
      }
    }
  ])
}

const progressEntry = (row: ProgressRow) => ({
  ref:
    row.subject.type === "occurrence"
      ? occurrenceRef(row.subject.id)
      : pageRef(row.subject.id),
  progress: progressRow(row)
})

function toSdkPage(pagePaths: PagePaths, payload: PagePayload): Page {
  const { id, progress, ...page } = payload

  pagePaths.remember(page.path, id, page.access?.root)

  // Progress leaves the payload for the store: `mvt.progress.status(path)` is
  // the one way to read it, and it stays live where a snapshot would not.
  if (progress && isPresent(id)) {
    seedProgress([
      { ref: pageRef(id as string), progress: progressRow(progress) }
    ])
  }

  return page
}

const ACTIVE_KEY = "active"

class Progress {
  private readonly guard: HydrationGuard
  private readonly activeGuard: HydrationGuard
  private readonly batch: ProgressBatch
  private activeContent: Array<Page & { id: string }> = []
  private activeFetchSeq = 0

  constructor(private readonly pagePaths: PagePaths) {
    const announce = () => eventManager.emit(MVT_EVENTS.PROGRESS_CHANGED)

    this.guard = new HydrationGuard(announce)
    this.activeGuard = new HydrationGuard(announce)
    this.batch = new ProgressBatch({
      read: pages => ApiService.getV2(ROUTES.progress(), { pages }),
      onRows: rows => this.learn(rows)
    })

    const invalidate = () => {
      this.guard.clear()
      this.activeGuard.clear()
      this.activeFetchSeq++
      this.activeContent = []
    }

    eventManager.on(MVT_EVENTS.LOGGED_IN, invalidate)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, invalidate)
    eventManager.on(MVT_EVENTS.PROGRESS_CHANGED, mutation => {
      if (mutation?.type === "pages/setPageSubscription") {
        this.activeGuard.forget(ACTIVE_KEY)
      } else if (mutation?.type === "pages/deletePageSubscription") {
        this.removeFromActive(mutation.payload)
      }
    })
  }

  status(subject: ProgressSubject): ProgressStatus {
    const resolved = resolveProgressSubject(subject)

    if ("occurrenceId" in resolved) {
      return this.occurrenceStatus(resolved.occurrenceId)
    }

    const { path } = resolved
    // The read is by bare path; only the completion below reads the occurrence.
    const bare = barePath(path) ?? path
    const uuid = this.pagePaths.known(bare)

    // Progress is member state, so a signed-out reader gets nothing
    const page =
      uuid && loggedIn()
        ? vuexStore.getters["pages/getProgress"](pageRef(uuid))
        : null

    // The read is by path, so a page the store has never seen hydrates from the
    // same response instead of staying a false zero. A listing that carried its
    // progress has already seeded it, and costs no request at all.
    if (loggedIn() && !page) {
      this.guard.ensure(bare, () => this.batch.fetch(bare))
    }

    const progress = page ?? {}

    // Completion is the subject's own; started and percent are always the
    // page's, whichever day is being asked about.
    const occurrence = occurrenceFor(path)

    const own =
      (occurrence && loggedIn()
        ? vuexStore.getters["pages/getProgress"](occurrenceRef(occurrence))
        : progress) || {}

    return {
      started: isPresent(progress.started_at),
      startedAt: progress.started_at ?? null,
      completed: !!own.is_completed,
      completedAt: own.completed_at ?? null,
      percent: progress.percent_complete ?? 0
    }
  }

  get active(): Page[] {
    if (!loggedIn()) return []

    this.hydrateActive()

    return this.activeContent
      .filter(item => !this.isComplete(item.id))
      .map(item => ({
        path: item.path,
        title: item.title ?? "",
        type: item.type
      }))
      .filter(page => isPresent(page.path))
  }

  destroy(): void {
    this.guard.destroy()
    this.activeGuard.destroy()
    this.batch.destroy()
  }

  async start(path: string, options: StartOptions = {}): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const startDate = isPresent(options.startedAt)
      ? toIsoDate(options.startedAt, "startedAt")
      : undefined

    const pageId = await this.pagePaths.resolve(path)

    await readCache.write(() =>
      vuexStore.dispatch("pages/subscribe", { pageId, startDate })
    )
    await this.refetchActive()
  }

  async complete(
    subject: ProgressSubject,
    options: CompleteOptions = {}
  ): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const completedAt = isPresent(options.completedAt)
      ? toIsoDate(options.completedAt, "completedAt")
      : undefined

    const resolved = resolveProgressSubject(subject)

    if ("occurrenceId" in resolved) {
      const id = resolved.occurrenceId
      const wasCompleted = this.occurrenceStatus(id).completed

      const progress: ProgressRow = await ApiService.postV2(ROUTES.progress(), {
        occurrence: id,
        ...(completedAt ? { completed_at: completedAt } : {})
      })

      this.learn([progress])

      if (!wasCompleted) {
        window.$mvt?.trackEvent(MVT_EVENTS.PROGRESS_COMPLETED, {
          path: null,
          completedAt: this.occurrenceStatus(id).completedAt
        })
      }

      return
    }

    const { path: bare, occurrence } = this.resolveSubject(resolved.path)
    const pageId = await this.pagePaths.resolve(bare)

    try {
      await readCache.write(() =>
        vuexStore.dispatch("pages/completePage", {
          pageId,
          occurrence,
          completedAt
        })
      )
    } catch (err) {
      if ((err as ApiError).response?.data?.reason === "invalid_completed_at") {
        throw codedError(
          "invalid_completed_at",
          "completedAt must be in the past"
        )
      }

      throw err
    }

    // percent arrives only via this refetch, and its failure must not reject a
    // completion that already succeeded.
    await this.guard
      .run(bare, () => this.batch.fetch(bare))
      .catch(() => undefined)
  }

  private occurrenceStatus(id: string): ProgressStatus {
    const own = loggedIn()
      ? vuexStore.getters["pages/getProgress"](occurrenceRef(id))
      : null

    const completed = !!own?.is_completed

    return {
      started: false,
      startedAt: null,
      completed,
      completedAt: own?.completed_at ?? null,
      percent: completed ? 100 : 0
    }
  }

  // The bare path, and which day of it the caller means.
  private resolveSubject(
    path: string
  ): {
    path: string
    occurrence: string | null
  } {
    return {
      path: barePath(path) ?? path,
      occurrence: occurrenceFor(path)
    }
  }

  // The inverse of `complete`, and the only one: `reset` unenrols and deletes
  // the subtree's progress, so it is not an undo.
  async uncomplete(subject: ProgressSubject): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const resolved = resolveProgressSubject(subject)

    if ("occurrenceId" in resolved) {
      const progress: ProgressRow = await ApiService.deleteV2(
        ROUTES.progress(),
        {
          occurrence: resolved.occurrenceId
        }
      )

      this.learn([progress])

      return
    }

    const { path: bare, occurrence } = this.resolveSubject(resolved.path)
    const pageId = await this.pagePaths.resolve(bare)

    await readCache.write(() =>
      vuexStore.dispatch("pages/uncompletePage", { pageId, occurrence })
    )
    await this.guard
      .run(bare, () => this.batch.fetch(bare))
      .catch(() => undefined)
  }

  /**
   * Destructive: drops the member's enrolment and every progress record beneath
   * this page (the whole subtree), back to never-started. This is NOT a
   * per-page un-complete — there is no way to un-tick a single page.
   */
  async reset(path: string): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const pageId = await this.pagePaths.resolve(path)

    await readCache.write(() => vuexStore.dispatch("pages/unsubscribe", pageId))
    this.removeFromActive(pageId)
  }

  private isComplete(uuid?: string): boolean {
    return isPresent(uuid)
      ? vuexStore.getters["pages/isPageComplete"](uuid)
      : false
  }

  private hydrateActive(): void {
    this.activeGuard.ensure(ACTIVE_KEY, () => this.fetchActive())
  }

  private async refetchActive(): Promise<void> {
    await this.activeGuard
      .run(ACTIVE_KEY, () => this.fetchActive())
      .catch(() => undefined)
  }

  private async fetchActive(): Promise<void> {
    const seq = ++this.activeFetchSeq

    const { active } = await ApiService.get("/progress/active", {})

    if (seq === this.activeFetchSeq && loggedIn()) {
      this.activeContent = Array.isArray(active) ? active : []
      eventManager.emit(MVT_EVENTS.PROGRESS_CHANGED)
    }
  }

  private removeFromActive(pageId?: string): void {
    if (!isPresent(pageId)) return
    this.activeContent = this.activeContent.filter(item => item.id !== pageId)
  }

  // One commit per batch: every commit re-renders every Code block reading
  // progress. The rows also teach path → uuid, so the next read is a sync hit.
  private learn(rows: ProgressRow[]): void {
    if (!rows.length || !loggedIn()) return

    rows.forEach(row => {
      if (row.subject.type === "page") {
        this.pagePaths.remember(row.subject.path, row.subject.id)
      }
    })
    vuexStore.commit("pages/setProgresses", rows.map(progressEntry))
  }
}

type SavedAction = "pages/addPageBookmark" | "pages/removePageBookmark"

class Streaks {
  private readonly last = new Map<string, string>()
  private readonly offProgress: () => void

  constructor() {
    const refresh = () => this.refresh()

    eventManager.on(MVT_EVENTS.PROGRESS_COMPLETED, refresh)
    this.offProgress = () =>
      eventManager.off(MVT_EVENTS.PROGRESS_COMPLETED, refresh)

    const forget = () => this.last.clear()

    eventManager.on(MVT_EVENTS.LOGGED_IN, forget)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, forget)
  }

  destroy(): void {
    this.offProgress()
  }

  async get(
    options: StreakOptions = {},
    onUpdate?: OnUpdate<Streak[] | null>
  ): Promise<Streak[] | null> {
    if (!loggedIn()) throw notAuthenticated()

    return await readCache.revalidating(
      stale => this.read(options, stale),
      onUpdate
    )
  }

  private async read(
    options: StreakOptions,
    stale: boolean
  ): Promise<Streak[] | null> {
    const params: Record<string, string> = {}

    if (isPresent(options.slug)) params.slug = options.slug as string

    if (isPresent(options.from))
      params.from = toCalendarDay(options.from, "from")

    if (isPresent(options.to)) params.to = toCalendarDay(options.to, "to")

    const data = await ApiService.get("/streaks", params, stale)

    const streaks = Array.isArray(data)
      ? data.map((streak: any) => ({
          slug: streak.slug,
          name: streak.name,
          interval: streak.interval,
          subjectIds: streak.subject_ids ?? [],
          current: streak.current,
          longest: streak.longest,
          days: (streak.days ?? []).map((day: any) => ({
            date: day.date,
            count: day.count,
            isFrozen: !!day.isFrozen
          }))
        }))
      : null

    this.notice(stableStringify(params), streaks)

    return streaks
  }

  private notice(key: string, streaks: Streak[] | null): void {
    const next = stableStringify(streaks)
    const changed = this.last.has(key) && this.last.get(key) !== next

    this.last.set(key, next)

    if (changed) eventManager.emit(MVT_EVENTS.STREAKS_CHANGED)
  }

  private refresh(): void {
    if (!loggedIn()) return

    readCache.forget("/streaks")

    for (const key of Array.from(this.last.keys())) {
      const options = JSON.parse(key) as StreakOptions

      this.read(options, false).catch(() => undefined)
    }
  }
}

// Saving is intent, not attainment — deliberately separate from `progress`,
// which is enrolment and completion.
class Saved {
  private readonly guard: HydrationGuard

  constructor(
    private readonly pagePaths: PagePaths,
    private readonly pages: Pages
  ) {
    this.guard = new HydrationGuard(() =>
      eventManager.emit(MVT_EVENTS.SAVED_CHANGED)
    )

    const invalidate = () => this.guard.clear()

    eventManager.on(MVT_EVENTS.LOGGED_IN, invalidate)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, invalidate)
  }

  destroy(): void {
    this.guard.destroy()
  }

  has(path: string): boolean {
    if (!loggedIn()) return false

    const uuid = this.pagePaths.known(path)

    if (!uuid) {
      this.hydrate(path)

      return false
    }

    return vuexStore.getters["pages/isPageBookmarked"](uuid)
  }

  // The member's saved pages, newest save first. Rides the content engine's
  // own saved filter, so it takes the same type/tag/cursor options as any page
  // query.
  async list(
    options: SavedListOptions = {},
    onUpdate?: OnUpdate<PagesQueryResult>
  ): Promise<PagesQueryResult> {
    if (!loggedIn()) return { results: [], total: 0 }

    return await readCache.revalidating(
      stale => this.pages.read({ ...options, saved: true }, stale),
      onUpdate
    )
  }

  async add(path: string): Promise<void> {
    await this.write("pages/addPageBookmark", path)
  }

  async remove(path: string): Promise<void> {
    await this.write("pages/removePageBookmark", path)
  }

  private async write(action: SavedAction, path: string): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const pageId = await this.pagePaths.resolve(path)

    await readCache.write(() => vuexStore.dispatch(action, pageId))
  }

  // Announces a newly resolved path so the read that asked for it re-runs.
  private hydrate(path: string): void {
    this.guard.ensure(path, async () => {
      const uuid = await this.pagePaths.lookup(path)

      if (uuid) eventManager.emit(MVT_EVENTS.SAVED_CHANGED)
    })
  }
}

class Schedule {
  constructor(private readonly pagePaths: PagePaths) {}

  async query(
    { from, to, timezone, type, select, context, mine }: ScheduleQueryOptions,
    onUpdate?: OnUpdate<Occurrence[]>
  ): Promise<Occurrence[]> {
    const types = Array.isArray(type) ? type : type ? [type] : []

    // from/to are whole calendar days; timezone is forwarded only when given so
    // the server applies its own member fallback otherwise.
    const params = {
      from: toDate(from, "YYYY-MM-DD"),
      to: toDate(to, "YYYY-MM-DD"),
      ...(timezone ? { timezone } : {}),
      ...(types.length ? { type: types } : {}),
      ...(select?.length ? { select } : {}),
      ...(isPresent(context) ? { context } : {}),
      ...(mine ? { mine: true } : {})
    }

    return await readCache.revalidating(
      stale => this.fetchAll(params, stale),
      onUpdate
    )
  }

  async move(
    occurrence: Occurrence | string | number,
    to: DateInput
  ): Promise<Occurrence> {
    if (!loggedIn()) throw notAuthenticated()

    const id = occurrenceId(occurrence)

    const data = await ApiService.putV2(ROUTES.scheduleItem(id), {
      starts_at: toStartsAt(to)
    })

    eventManager.emit(MVT_EVENTS.SCHEDULE_CHANGED)

    return this.toSdkOccurrence(data)
  }

  async add(
    target: string | ScheduleRowRef,
    on: DateInput,
    options: ScheduleAddOptions = {}
  ): Promise<Occurrence> {
    if (!loggedIn()) throw notAuthenticated()

    const data = await ApiService.op(WRITE_OPS.scheduleAdd, {
      ...this.subject(target),
      ...this.item(on, options)
    })

    return this.toSdkOccurrence(data)
  }

  async bulkAdd(items: ScheduleBulkAddItem[]): Promise<Occurrence[]> {
    if (!loggedIn()) throw notAuthenticated()

    if (!items?.length) return []

    if (items.length > MAX_BULK_SCHEDULE_ITEMS) {
      throw codedError(
        "too_many_items",
        `No more than ${MAX_BULK_SCHEDULE_ITEMS} items can be scheduled at once.`
      )
    }

    const data = await ApiService.postV2(ROUTES.scheduleItemsBulk(), {
      items: items.map(({ path, on, options }) => ({
        page: path,
        ...this.item(on, options)
      }))
    })

    const occurrences: Occurrence[] = (
      data?.results ?? []
    ).map((occurrence: Occurrence) => this.toSdkOccurrence(occurrence))

    if (occurrences.length) eventManager.emit(MVT_EVENTS.SCHEDULE_CHANGED)

    const errors: ScheduleBulkAddError[] = (data?.errors ?? []).map(
      (error: { index: number; code: string; message: string }) => ({
        index: error.index,
        path: items[error.index]?.path,
        error: error.code,
        reason: error.message
      })
    )

    if (errors.length) throw this.partialRefusal(occurrences, errors)

    return occurrences
  }

  async remove(occurrence: Occurrence | string | number): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    return ApiService.op(WRITE_OPS.scheduleRemove, {
      id: occurrenceId(occurrence)
    })
  }

  private subject(target: string | ScheduleRowRef) {
    if (typeof target === "string") return { page: target }

    if (!isPresent(target?.table) || !isPresent(target?.id)) {
      throw codedError("not_found", "A row needs a table and an id")
    }

    return { row: { table: target.table, id: target.id } }
  }

  private item(on: DateInput, options: ScheduleAddOptions = {}) {
    return {
      starts_at: toStartsAt(on),
      ...(options.isAllDay === undefined
        ? {}
        : { is_all_day: options.isAllDay }),
      ...(isPresent(options.context) ? { context: options.context } : {})
    }
  }

  private partialRefusal(
    occurrences: Occurrence[],
    errors: ScheduleBulkAddError[]
  ): ScheduleBulkAddFailure {
    const total = occurrences.length + errors.length

    const error = codedError(
      "partial_failure",
      `${errors.length} of ${total} could not be scheduled`
    )

    return Object.assign(error, {
      occurrences,
      errors
    }) as ScheduleBulkAddFailure
  }

  // The payload already knows every occurrence's standing, so seeding it here
  // is what lets a member's calendar answer without a request per entry.
  private toSdkOccurrence(occurrence: OccurrencePayload): Occurrence {
    const { progress, ...rest } = occurrence

    seedOccurrenceProgress(rest, progress)

    if (!rest.page) return rest

    return {
      ...rest,
      page: toSdkPage(this.pagePaths, rest.page)
    }
  }

  private async fetchAll(
    params: Record<string, any>,
    stale: boolean
  ): Promise<Occurrence[]> {
    const occurrences = await ApiService.walkV2(
      ROUTES.schedule(),
      params,
      stale
    )

    return occurrences.map((occurrence: Occurrence) =>
      this.toSdkOccurrence(occurrence)
    )
  }
}

const MAX_PAGE_SIZE = 20

// A date-only value stays a date so the server resolves it in the member's timezone.
function toStartsAt(to: DateInput): string {
  if (typeof to === "string" && DATE_ONLY.test(to)) return to

  return toIsoDate(to, "date")
}

// An authoring typo keeps its own coded error rather than a round-trip.
function assertWithin(within: string): void {
  if (/^(\d+)([hdw])$/.test(within)) return

  throw codedError(
    "invalid_duration",
    `schedule.within expects "<n>h" | "<n>d" | "<n>w", got: ${within}`
  )
}

// The content-plane sibling of $mvt.tables: queries the member-navigable page
// space through GET /v2/pages, so results are access-scoped pages with paths —
// never raw rows.
class Pages {
  constructor(private readonly pagePaths: PagePaths) {}

  async query(
    options: PagesQueryOptions = {},
    onUpdate?: OnUpdate<PagesQueryResult>
  ): Promise<PagesQueryResult> {
    return await readCache.revalidating(
      stale => this.read(options, stale),
      onUpdate
    )
  }

  async read(
    options: PagesQueryOptions,
    stale: boolean
  ): Promise<PagesQueryResult> {
    const data = await ApiService.getV2(
      ROUTES.pages(),
      this.toParams(options),
      stale
    )

    return {
      results: (data?.results ?? []).map((page: PagePayload) =>
        toSdkPage(this.pagePaths, page)
      ),
      total: data?.total ?? 0,
      ...(isPresent(data?.next_cursor) ? { nextCursor: data.next_cursor } : {})
    }
  }

  private toParams(options: PagesQueryOptions): Record<string, any> {
    // Authored code written against the old surface would otherwise page
    // forever on page one, so say so instead.
    if ("page" in options) {
      throw codedError(
        "invalid_query",
        "pages.query no longer takes `page` — hand the previous result's `nextCursor` back as `cursor`."
      )
    }

    // The context page is explicit (`of`); passing it alone means "children".
    const params: Record<string, any> = {
      type: options.type ?? "any",
      scope: options.scope ?? (options.of ? "children" : "app"),
      limit: Math.min(options.limit ?? MAX_PAGE_SIZE, MAX_PAGE_SIZE)
    }

    if (options.cursor) params.cursor = options.cursor

    // Sent only when asked for: the engine ranks a bare `search` by relevance,
    // which it can't do if every query claims an explicit order.
    if (options.order) params.order = options.order

    if (options.of) params.of = options.of

    if (options.tags?.length) {
      params.tags = options.tags
      params.match = options.match ?? "any"
    }

    if (options.excludeTags?.length) params.exclude_tags = options.excludeTags

    if (options.where) params.where = options.where

    // An empty box is "no filter", not "match nothing".
    if (options.search?.trim()) params.search = options.search.trim()

    if (options.select?.length) params.select = options.select

    if (options.saved) params.saved = true

    const schedule = options.schedule

    if (schedule) {
      if (schedule.within) assertWithin(schedule.within)

      params.schedule = {
        when: schedule.when,
        ...(schedule.within ? { within: schedule.within } : {}),
        ...(schedule.calendar ? { calendar: schedule.calendar } : {}),
        ...(schedule.timezone ? { timezone: schedule.timezone } : {}),
        ...(schedule.includeCurrent === undefined
          ? {}
          : { include_current: schedule.includeCurrent })
      }
    }

    return params
  }
}

// Re-minted only when a projected field moves, so an authored watch doesn't
// fire on an avatar or product-id refresh.
const createUserProjection = (): (() => MvtUser | null) => {
  let user: MvtUser | null = null

  return () => {
    const current = vuexStore.getters["auth/currentUser"]

    const id = vuexStore.getters["auth/isAuthenticated"]
      ? current?.subscriberUuid || null
      : null

    if (!id) {
      user = null
    } else if (
      id !== user?.id ||
      current.name !== user?.name ||
      current.email !== user?.email
    ) {
      user = Object.freeze({ name: current.name, email: current.email, id })
    }

    return user
  }
}

class Auth {
  constructor(
    private readonly events: EventManager,
    private readonly currentUser: () => MvtUser | null
  ) {}

  get ready(): boolean {
    return !!vuexStore.getters["auth/hasCheckedAuthentication"]
  }

  whenReady(): Promise<MvtUser | null> {
    if (this.ready) return Promise.resolve(this.currentUser())

    return new Promise(resolve =>
      this.events.once(MVT_EVENTS.AUTH_READY, () => resolve(this.currentUser()))
    )
  }

  async login() {
    router.push("#auth")
  }

  async logout() {
    await vuexStore.dispatch("auth/logout")
  }
}

const GRANTED_STATE: AccessState = { status: "granted" }
const UNKNOWN_STATE: AccessState = { status: "unknown" }

const currentPageId = (): PageId | null =>
  barePath(currentPagePath()) === barePath(router.currentRoute.path)
    ? (vuexStore as any).state?.pages?.currentPage?.id ?? null
    : null

const hasAccessRules = (pageId: PageId): boolean =>
  isPresent(vuexStore.getters["pages/getPageAccess"](pageId))

class Access {
  private readonly guard: HydrationGuard
  private readonly pageIds = new Map<string, PageId | null>()
  private session = 0
  private announcement: ReturnType<typeof setTimeout> | null = null

  private readonly announce = () => {
    if (this.announcement) return

    this.announcement = setTimeout(() => {
      this.announcement = null
      eventManager.emit(MVT_EVENTS.ACCESS_CHANGED)
    }, ANNOUNCE_DELAY_MS)
  }

  constructor(private readonly pagePaths: PagePaths) {
    this.guard = new HydrationGuard(this.announce)

    const invalidate = () => {
      this.session++
      this.guard.clear()
      this.pageIds.clear()
      this.announce()
    }

    eventManager.on(MVT_EVENTS.LOGGED_IN, invalidate)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, invalidate)
    eventManager.on(MVT_EVENTS.PRODUCTS_CHANGED, this.announce)
    eventManager.on(MVT_EVENTS.PROGRESS_CHANGED, this.announce)
  }

  check(path: string): AccessState {
    const target = String(path ?? "").split(/[?#]/)[0]

    if (skipsAccessChecks(target)) return GRANTED_STATE

    this.guard.ensure(target, () => this.hydrate(target))

    const pageId = this.pageIds.get(target)

    if (pageId === undefined) return UNKNOWN_STATE

    // A path no page answers to is a redirect or a static route.
    if (pageId === null) return GRANTED_STATE

    return toAccessState(
      getPageAccess({
        pageId,
        currentPageId: this.routedPageId() ?? pageId,
        appAccess: vuexStore.getters.appAccess
      })
    )
  }

  destroy(): void {
    this.guard.destroy()

    if (this.announcement) clearTimeout(this.announcement)
    this.announcement = null
  }

  private routedPageId(): PageId | null {
    return currentPageId() ?? this.pagePaths.known(router.currentRoute.path)
  }

  private async hydrate(path: string): Promise<void> {
    const session = this.session
    const named = this.pagePaths.known(path)

    const pageId =
      named &&
      (isPresent(this.pagePaths.rootOf(named)) || hasAccessRules(named))
        ? named
        : await this.pagePaths.lookup(path)

    if (isPresent(pageId)) {
      await loadPageAccessTree(pageId, this.pagePaths.rootOf(pageId))
    }

    if (session !== this.session) return

    this.pageIds.set(path, pageId)
    this.announce()
  }
}

class Sdk {
  readonly MVT_EVENTS = MVT_EVENTS
  readonly auth: Auth
  readonly tables: Record<string, Table>
  readonly pages: Pages
  readonly schedule: Schedule
  readonly native: NativeBridge
  readonly cast: Cast
  readonly products: ProductsApi
  readonly progress: Progress
  readonly streaks: Streaks
  readonly saved: Saved
  readonly access: Access
  readonly userProperties: Record<string, unknown>
  private readonly currentUser: () => MvtUser | null
  private readonly stopRouteWatch: () => void

  constructor() {
    // Cached reads belong to a session, and a member switch ends one.
    readCache.reset()
    eventManager.on(MVT_EVENTS.LOGGED_IN, () => readCache.reset())
    eventManager.on(MVT_EVENTS.LOGGED_OUT, () => readCache.reset())

    const pagePaths = new PagePaths()

    this.currentUser = createUserProjection()
    this.auth = new Auth(eventManager, this.currentUser)
    this.pages = new Pages(pagePaths)
    this.schedule = new Schedule(pagePaths)
    this.native = new NativeBridge()
    this.cast = new Cast(
      this.native,
      () => vuexStore.state.global?.cast?.providers ?? [],
      () => eventManager.emit(MVT_EVENTS.CAST_CHANGED)
    )
    this.progress = new Progress(pagePaths)
    this.streaks = new Streaks()
    this.saved = new Saved(pagePaths, this.pages)
    this.access = new Access(pagePaths)

    const cache: Record<string, Table> = {}

    this.tables = new Proxy(cache, {
      get(target, slug) {
        if (typeof slug !== "string") return undefined

        return target[slug] || (target[slug] = new Table(slug))
      }
    })

    this.products = new ProductsApi()

    const submittedProperties = (): Record<string, unknown> =>
      isPresent(vuexStore.getters["auth/currentUser"])
        ? vuexStore.getters[
            "subscriberProperties/subscriberSubmittedProperties"
          ]
        : {}

    this.userProperties = new Proxy(
      {},
      {
        get: (_target, key) => {
          if (typeof key !== "string") return undefined

          if (!isPresent(vuexStore.getters["auth/currentUser"])) return null

          return submittedProperties()[key] ?? null
        },
        has: (_target, key) =>
          typeof key === "string" && key in submittedProperties(),
        ownKeys: () => Reflect.ownKeys(submittedProperties()),
        getOwnPropertyDescriptor: (_target, key) =>
          typeof key === "string" && key in submittedProperties()
            ? {
                value: submittedProperties()[key],
                enumerable: true,
                configurable: true
              }
            : undefined,
        set: (_target, key, value) => {
          if (typeof key !== "string") return false

          if (!isPresent(vuexStore.getters["auth/currentUser"])) return true

          readCache
            .write(() =>
              vuexStore.dispatch(
                "subscriberProperties/setPropertyOptimistically",
                {
                  slug: key,
                  value
                }
              )
            )
            .catch((error: unknown) => {
              eventManager.emit(MVT_EVENTS.USER_PROPERTIES_ERROR, {
                key,
                value,
                error
              })
            })

          return true
        }
      }
    )

    const stopRouteWatch = router.afterEach(() =>
      eventManager.emit(MVT_EVENTS.ROUTE_CHANGED)
    )

    this.stopRouteWatch = () => stopRouteWatch()
  }

  destroy(): void {
    this.stopRouteWatch()
    this.native.destroy()
    this.progress.destroy()
    this.streaks.destroy()
    this.saved.destroy()
    this.access.destroy()
  }

  get events() {
    return eventManager
  }

  trackEvent(eventName: string, payload: TrackEventPayload = {}): void {
    const enrichedPayload: TrackEventPayload = {
      timestamp: Date.now(),
      ...payload
    }

    // Absent rather than null for a guest: the GTM layer fills in an anonymous
    // visitor's subscriber id only when this key is missing.
    const user = this.user

    if (user && !("user" in enrichedPayload)) {
      enrichedPayload.user = user
    }

    eventManager.emit(eventName, enrichedPayload)
  }

  get user(): MvtUser | null {
    return this.currentUser()
  }

  get page() {
    const currentPage = (vuexStore as any).state?.pages?.currentPage || {}

    const pageData = currentPage.id
      ? vuexStore.getters["pages/getPageData"](currentPage.id) || {}
      : {}

    return {
      props: Object.freeze(
        pageDataToProps({
          pageData,
          builtInFieldContext: {
            pageTitle: currentPage.title,
            currentSchedule: currentPage.currentSchedule
          }
        })
      ),
      occurrence: this.ambientOccurrence()
    }
  }

  // Which day the page was opened for. Its completion is read the one way
  // everything else is: mvt.progress.status(path).
  private ambientOccurrence(): {
    id: string
    startsAt: string | null
  } | null {
    const id = ambientOccurrenceId()

    if (!id) return null

    return {
      id,
      startsAt:
        vuexStore.getters["pages/getProgress"](occurrenceRef(id))?.starts_at ??
        null
    }
  }

  // Kept for backwards compat; the agent is taught mvt.auth.login / logout.
  async logout() {
    await this.auth.logout()
  }

  async login() {
    await this.auth.login()
  }

  navigate(path: string, options: NavigateOptions = {}): void {
    if (!isPresent(path)) {
      throw new Error("$mvt.navigate expects a path")
    }

    if (isWebLink(path)) {
      window.open(path, "_blank")

      return
    }

    if (!path.startsWith("/") || path.startsWith("//")) {
      throw new Error(
        `$mvt.navigate expects an in-app path starting with "/", got: ${path}`
      )
    }

    const go = () => {
      const navigation = options.replace
        ? router.replace(path)
        : router.push(path)

      // Swallow redirected/duplicate navigation rejections (e.g. navigating
      // to the current path) so authored code never sees an unhandled one.
      navigation.catch(() => undefined)
    }

    // Managed routes have no page to gate, and answering them synchronously
    // keeps the SDK's existing contract for them.
    if (path.startsWith(MANAGED_PAGE_PREFIX)) return go()

    // Lazily imported: the gate must stay out of the eagerly-loaded graph.
    import("@/lib/access/navigation")
      .then(({ navigateWithAccessChecks }) =>
        navigateWithAccessChecks(path, { go })
      )
      .catch(go)
  }

  get route(): MvtRoute {
    const { path, params, query } = router.currentRoute

    return { path, params, query }
  }
}

export default Sdk
